import * as React from 'react';
import { UserContext } from '@app/Context/UserContext';
import ReportDataHelper from '@app/Service/Report/ReportDataHelper';
import ContractInterface from '@app/Interface/Report/ContractInterface';
import { Button, Select, SelectOption, SelectOptionObject, Text, TextContent } from '@patternfly/react-core';
import DownloadIcon from '@patternfly/react-icons/dist/js/icons/download-icon';
import ItemLoader from '@app/Components/ItemLoader';
import TabulatorFormatters from '@app/utils/TabulatorFormatters';
import TabulatorMutators from '@app/utils/TabulatorMutators';
import moment from 'moment-timezone';
import { useMediaQuery } from '@react-hook/media-query';

import SCTabulator from '@app/utils/SCTabulator';
import options from '@app/utils/SCTabulatorCommonOptions';
import TabulatorAccessors from '@app/utils/TabulatorAccessors';

interface FCPSettlementsReportProps {
    pageId: number,
    setLastUpdatedText: React.Dispatch<React.SetStateAction<string>>;
    setReportTableRef: React.Dispatch<React.SetStateAction<React.MutableRefObject<null> | null>>;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setReportPdfLink: React.Dispatch<React.SetStateAction<string>>;
    loading: boolean;
}

const FCPSettlementsReport: React.FC<FCPSettlementsReportProps> = ({ pageId, setLastUpdatedText, setReportTableRef, setLoading, setReportPdfLink, loading }) => {
    // In order to get tabulator to work with moment-timezone
    // we need to add moment to the global namespace.
    // see: https://stackoverflow.com/a/54135475/8038146
    window.moment = moment;

    const isSmallScreen = useMediaQuery('only screen and (max-width: 800px)');

    const { userState } = React.useContext(UserContext);

    // Update the parent report page's download CSV function to tap into our report's tabulator ref
    const tableRef = React.useCallback(node => {
        setReportTableRef({current: node});
    }, []);
    const [reportData, setReportData] = React.useState<ContractInterface[]>([]);
    const [filteredReportData, setFilteredReportData] = React.useState<ContractInterface[]>([]);
    const [filteredReportDataHasEstimates, setFilteredReportDataHasEstimates] = React.useState(true);
    const [estimateDate, setEstimateDate] = React.useState<moment.Moment>(moment());
    const [reportMonthYears, setReportMonthYears] = React.useState<string[]>([]);
    const [selectedMonthYear, setSelectedMonthYear] = React.useState<string | SelectOptionObject>('All');
    const [isMonthYearOpen, setIsMonthYearOpen] = React.useState(false);

    // Calculate the estimate date on the first render and store for future use
    React.useEffect(() => {
        let calculatedEstimateDate = (() => {
            const getLastPriorBusinessDayDate = (dateToExamine: moment.Moment) => {
                // If the date is a weekday (i.e. iso weekday 1-5)
                if (dateToExamine.isoWeekday() < 6) {
                    return dateToExamine;
                } else {
                    return getLastPriorBusinessDayDate(dateToExamine.subtract(1, 'day'));
                }
            }

            // Begin with the start date of the current month
            let estimateDate = moment().startOf('month');

            // if the current date is equal to or less than the last business day prior to the 15th, set the estimate month back by one month
            const lastPriorBusinessDay = getLastPriorBusinessDayDate(moment().startOf('month').date(15));
            if (moment().isSameOrBefore(lastPriorBusinessDay)) {
                estimateDate.subtract(1, 'month');
            }

            return estimateDate;
        })();
        setEstimateDate(calculatedEstimateDate);
    }, []);

    // Tabulator calculator function for weighted price gains/losses
    const weightedPriceGainLossCalculator = (values, data: ContractInterface[], calcParams) => {
        const totalContractGainLoss = data.reduce(
            (total, row) => {
                const rowTotalNetChange = Number.isNaN(row.Total_Net_Change)
                    ? 0
                    : Number(row.Total_Net_Change);
                return total + rowTotalNetChange;
            },
            0
        );
        const totalMilkVolume = data.reduce(
            (total, row) => {
                const rowMilkVolume = Number.isNaN(row.Milk_Volume)
                    ? 0
                    : Number(row.Milk_Volume);
                return total + rowMilkVolume;
            },
            0
        );

        return totalContractGainLoss && totalMilkVolume
            ? (totalContractGainLoss / totalMilkVolume) * 100
            : '';
    };

    // TODO: Will we eventually add search? Requirements say that's a future phase.    
    // Used when the report contains no estimate data, and should display a totals row 
    const tabulatorColumnsWithTotals = [
        {
            title: 'Contract Month-Year', field: 'Contract_Date', formatter: 'datetime', formatterParams: {
                outputFormat: 'MMM-YYYY',
            }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor
        },
        { title: 'Description', field: 'Contract_Description', accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        {
            title: 'Date', field: 'Transaction_Date', formatter: 'datetime', formatterParams: {
                outputFormat: 'MM/DD/YYYY',
            }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor
        },
        {
            title: 'Volume', field: 'Milk_Volume', formatter: 'money', formatterParams: {
                precision: false
            }, bottomCalc: 'sum', bottomCalcFormatter: 'money', bottomCalcFormatterParams: { precision: false }, 
            accessorDownload: TabulatorAccessors.nullValueDownloadAccessor
        },
        {
            title: 'Contract BFP per CWT', field: 'Offer_Price', mutator: TabulatorMutators.timesHundredMutator, formatter: TabulatorFormatters.accountingCurrencyFormatter, formatterParams: {
                symbol: '$'
            }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor
        },
        {
            title: 'Actual BFP per CWT', field: 'Settlement_Price', mutator: TabulatorMutators.timesHundredMutator, formatter: TabulatorFormatters.accountingCurrencyFormatter, formatterParams: {
                symbol: '$'
            }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor
        },
        {
            title: 'Gain/(Loss) per CWT', field: 'Net_Change', mutator: TabulatorMutators.timesHundredMutator, formatter: TabulatorFormatters.accountingCurrencyFormatter, formatterParams: {
                symbol: '$'
            }, bottomCalc: weightedPriceGainLossCalculator, bottomCalcFormatter: TabulatorFormatters.accountingCurrencyFormatter, bottomCalcFormatterParams: { symbol: '$' }, 
            accessorDownload: TabulatorAccessors.nullValueDownloadAccessor
        },
        {
            title: 'Contract Gain/(Loss)', field: 'Total_Net_Change', formatter: TabulatorFormatters.accountingCurrencyFormatter, formatterParams: {
                symbol: '$'
            }, bottomCalc: 'sum', bottomCalcFormatter: TabulatorFormatters.accountingCurrencyFormatter, bottomCalcFormatterParams: { symbol: '$' }, 
            accessorDownload: TabulatorAccessors.nullValueDownloadAccessor
        },
    ];

    // Used when the report contains one or more estimate rows, and should not display a totals row
    const tabulatorColumnsWithoutTotals = [
        {
            title: 'Contract Month-Year', field: 'Contract_Date', formatter: 'datetime', formatterParams: {
                outputFormat: 'MMM-YYYY',
            }
        },
        { title: 'Description', field: 'Contract_Description' },
        {
            title: 'Date', field: 'Transaction_Date', formatter: 'datetime', formatterParams: {
                outputFormat: 'MM/DD/YYYY',
            }
        },
        {
            title: 'Volume', field: 'Milk_Volume', formatter: 'money', formatterParams: {
                precision: false
            }
        },
        {
            title: 'Contract BFP per CWT', field: 'Offer_Price', mutator: TabulatorMutators.timesHundredMutator, formatter: TabulatorFormatters.accountingCurrencyFormatter, formatterParams: {
                symbol: '$'
            }
        },
        {
            title: 'Actual BFP per CWT', field: 'Settlement_Price', formatter: TabulatorFormatters.fcpSettlementEstimateFormatter, formatterParams: {
                symbol: '$'
            }, mutator: TabulatorMutators.fcpSettlementEstimateMutator, mutatorParams: { estimateDate: estimateDate, multiply: true }
        },
        {
            title: 'Gain/(Loss) per CWT', field: 'Net_Change', formatter: TabulatorFormatters.fcpSettlementEstimateFormatter, formatterParams: {
                symbol: '$'
            }, mutator: TabulatorMutators.fcpSettlementEstimateMutator, mutatorParams: { estimateDate: estimateDate, multiply: true }
        },
        {
            title: 'Contract Gain/(Loss)', field: 'Total_Net_Change', formatter: TabulatorFormatters.fcpSettlementEstimateFormatter, formatterParams: {
                symbol: '$'
            }, mutator: TabulatorMutators.fcpSettlementEstimateMutator, mutatorParams: { estimateDate: estimateDate, multiply: false }
        },
    ];

    // Set last updated text
    React.useEffect(() => {
        const controller = new AbortController();

        let mounted = true;

        ReportDataHelper
            .getTableUpdatedText(controller, 'ndaWeb_FcpClosed')
            .then(text => { if (mounted) setLastUpdatedText(text) });

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, []);

    const getSettlementLetterPdfUrl = () => {
        const selectedMonthYearMoment = moment(selectedMonthYear.toString(), 'MMM-YYYY');
        return ReportDataHelper.getReportPdfUrl(
            'fcp-settlements-letter',
            userState.user.currentFpuId,
            selectedMonthYearMoment.format('MM/YYYY')
        );
    }

    const getUniqueMonthYearsFromRows = (rows: ContractInterface[]) => {
        const uniqueMonthYears = {};
        rows.forEach(row => {
            const contractDate = moment(row.Contract_Date).startOf('month');
            if (!uniqueMonthYears[contractDate.format('MMM-YYYY')]) {
                uniqueMonthYears[contractDate.format('MMM-YYYY')] = contractDate.format('MMM-YYYY');
            }
        });
        return Object.values<string>(uniqueMonthYears);
    };

    const getMonthYearOptions = () => {
        return [
            <SelectOption key={-1} value="All" />,
            ...reportMonthYears.map((monthYear, index) =>
                <SelectOption key={index} value={monthYear} />
            )
        ]
    };

    // Filters the report data based on the selected month/year
    const getFilteredReportData = () => {
        if (selectedMonthYear === 'All') {
            return reportData;
        }

        const selectedMonthYearMoment = moment(selectedMonthYear.toString(), 'MMM-YYYY')
        return reportData.filter(row => moment(row.Contract_Date).isSame(selectedMonthYearMoment, 'month'));
    }

    // When the page, user, or FPU change, fetch new report data
    React.useEffect(() => {
        const controller = new AbortController();

        let mounted = true;

        async function fetchReportData() {
            if (userState.user?.currentFpuId > 0) {
                const rows = await ReportDataHelper.getContractData(controller, userState.user.currentFpuId, pageId, true);
                if (mounted) {
                    setReportData(rows);
                    setReportMonthYears(getUniqueMonthYearsFromRows(rows));
                    setSelectedMonthYear('All');
                    setLoading(false);
                }
            } else {
                if (mounted) {
                    setReportData([]);
                    setReportMonthYears([]);
                    setSelectedMonthYear('All');
                    setLoading(false);
                }
            }
        }

        setLoading(true);
        fetchReportData();

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [pageId, userState.user.id, userState.user.currentFpuId]);

    // Filter the report data, check if it has estimates, and update PDF link when appropriate
    React.useEffect(() => {
        const filteredData = getFilteredReportData();
        // The report has estimates if the data being viewed has at least 1 row with a description containing the
        // substring "Total Price" and a contract date matching the calculated estimate date
        const hasEstimates = filteredData.some(
            row => row.Contract_Description.includes('Total Price') &&
                moment(row.Contract_Date).format('MMM-YYYY') === estimateDate.format('MMM-YYYY')
        );
        const selectedMonthYearMoment = selectedMonthYear === 'All'
            ? null
            : moment(selectedMonthYear.toString(), 'MMM-YYYY');
        const pdfLink = filteredData.length === 0
            ? ''
            : ReportDataHelper.getReportPdfUrl(
                'fcp-settlements',
                userState.user.currentFpuId,
                selectedMonthYearMoment ? selectedMonthYearMoment.format('MM/YYYY') : undefined
            );

        setFilteredReportData(filteredData);
        setFilteredReportDataHasEstimates(hasEstimates);
        setReportPdfLink(pdfLink);
    }, [reportData, selectedMonthYear]);

    if (loading) {
        return <ItemLoader />;
    }

    if (reportData.length === 0) {
        return <p><em>No report data to show for the selected producer</em></p>;
    }

    return (
        <>
            {filteredReportDataHasEstimates &&
                <TextContent className="pf-u-danger-color-100 pf-u-mb-lg">
                    <Text>
                        Settlement Prices have not yet been finalized for your closed Total Price contracts during the month of {estimateDate.format('MMM-YYYY')}. Please check back after the 15th for your settlement letter.
                    </Text>
                </TextContent>
            }
            {!filteredReportDataHasEstimates && selectedMonthYear !== 'All' &&
                <Button className="pf-u-float-right" icon={<DownloadIcon />} component="a" target="_blank" href={getSettlementLetterPdfUrl()}>
                    Download Settlement Letter
                </Button>
            }
            <p>Contract Month-Year:</p>
            <Select
                width="50%"
                className="pf-u-mb-lg"
                onToggle={isExpanded => { setIsMonthYearOpen(isExpanded) }}
                isOpen={isMonthYearOpen}
                selections={selectedMonthYear}
                onSelect={(e, value) => {
                    setSelectedMonthYear(value);
                    setIsMonthYearOpen(false);
                }}
            >
                {getMonthYearOptions()}
            </Select>
            <SCTabulator
                tableRef={tableRef}
                data={reportData}
                columns={filteredReportDataHasEstimates ? tabulatorColumnsWithoutTotals : tabulatorColumnsWithTotals}
                layout={isSmallScreen ? 'fitData' : 'fitColumns'}
                options={{
                    ...options('nda-report-fcp-settlements-sc', true, 'local')
                }}
            />
        </>
    );
};

export default FCPSettlementsReport;